import { Box, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { ClinicalRecord } from "../../hooks/useClinicalRecords";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import RecordCard from "./RecordCard";

interface Props {
  activeRecords: ClinicalRecord[];
  isNotes: boolean;
  onEdit: (record: ClinicalRecord) => void;
  onAdd: () => void;
}

export default function RecordsList({
  activeRecords,
  isNotes,
  onEdit,
  onAdd
}: Props) {
  const { t } = useTranslation();

  return (
    <>
      {activeRecords.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            bgcolor: semanticColors.surface.canvas,
            borderRadius: "20px",
            boxShadow: "0px 0px 4px rgba(30,39,79,0.08)"
          }}
        >
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.secondary }}
          >
            {isNotes
              ? t("clinicalRecords.emptyNotes")
              : t("clinicalRecords.emptySummaries")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {activeRecords.map(rec => (
            <RecordCard
              key={rec.id}
              record={rec}
              typeLabel={
                isNotes
                  ? t("clinicalRecords.noteType")
                  : t("clinicalRecords.summaryType")
              }
              noContentLabel={t("clinicalRecords.noContent")}
              onClick={() => onEdit(rec)}
            />
          ))}
        </Box>
      )}

      <Tooltip
        title={
          isNotes
            ? t("clinicalRecords.addNoteTooltip")
            : t("clinicalRecords.addSummaryTooltip")
        }
      >
        <Box
          component="button"
          onClick={onAdd}
          sx={{
            position: "fixed",
            bottom: "40px",
            right: { xs: "20px", md: "40px" },
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: semanticColors.gradient.surface,
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(13,189,167,0.4)",
            zIndex: 20,
            "&:hover": { opacity: 0.9 }
          }}
        >
          <AddIcon sx={{ color: semanticColors.text.inverse, fontSize: 28 }} />
        </Box>
      </Tooltip>
    </>
  );
}
